import { formatCurrencyCompact, formatPct2 } from '@/shared/utils/number';
import { getDefaultThresholdFromConfig } from '@/features/settings/utils/resolveDashboardSetting';

const GREEN = '#16a34a';
const RED = '#dc2626';
const C_GRAY = '#cbd5e1';

export interface ComplianceBarProps {
  /** A+B share of (A+B+C), already rounded to 2dp (away from zero). */
  abShare: number;
  /** C share of (A+B+C), already rounded to 2dp. */
  cShare: number;
  compliantAB: boolean;
  threshold?: number;
  aMain: number;
  bMain: number;
  cMain: number;
  className?: string;
}

export function ComplianceBar({
  abShare,
  cShare,
  compliantAB,
  threshold = getDefaultThresholdFromConfig('Budget Law A+B Minimum Percent') / 100,
  aMain,
  bMain,
  cMain,
  className = '',
}: ComplianceBarProps) {
  const thr = Number.isFinite(threshold)
    ? threshold
    : getDefaultThresholdFromConfig('Budget Law A+B Minimum Percent') / 100;
  const ab = Number.isFinite(abShare) ? Math.min(1, Math.max(0, abShare)) : 0;
  const c = Number.isFinite(cShare) ? Math.min(1, Math.max(0, cShare)) : 0;
  const abColor = compliantAB ? GREEN : RED;
  const abPctW = ab * 100;
  const cPctW = c * 100;
  const markerLeftPct = Math.min(100, Math.max(0, thr * 100));

  const abMain = aMain + bMain;

  const ariaLabel = `Cost-bucket compliance. A plus B ${formatPct2(ab)} of A plus B plus C, C ${formatPct2(
    c
  )}. Rule at least ${formatPct2(thr)}. D excluded. ${compliantAB ? 'Compliant.' : 'Non-compliant.'}`;

  const legend: Array<{ key: string; label: string; color: string; amount: number; share: number }> = [
    { key: 'ab', label: 'A+B (Salaries + Related)', color: abColor, amount: abMain, share: ab },
    { key: 'c', label: 'C (Operations)', color: C_GRAY, amount: cMain, share: c },
  ];

  return (
    <div className={className}>
      <div
        className="relative flex h-12 w-full overflow-hidden rounded-lg bg-slate-200 ring-2 ring-slate-300/80 print:h-9"
        role="img"
        aria-label={ariaLabel}
      >
        <div
          className="h-full shrink-0 transition-[width] duration-300 ease-out"
          style={{ width: `${abPctW}%`, backgroundColor: abColor }}
        />
        <div
          className="h-full shrink-0 transition-[width] duration-300 ease-out"
          style={{ width: `${cPctW}%`, backgroundColor: C_GRAY }}
        />
        <span
          className="pointer-events-none absolute top-0 z-10 h-full w-px bg-slate-950 shadow-[1px_0_0_rgba(255,255,255,0.45)]"
          style={{ left: `${markerLeftPct}%`, transform: 'translateX(-50%)' }}
          aria-hidden
        />
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[10px] font-semibold text-slate-700 print:text-[8px]">
        {legend.map((seg) => (
          <li key={seg.key} className="inline-flex items-center gap-1.5">
            <span
              className="size-2.5 shrink-0 rounded-sm ring-1 ring-black/10"
              style={{ backgroundColor: seg.color }}
            />
            <span>{seg.label}</span>
            <span className="font-normal text-slate-500">
              {formatCurrencyCompact(seg.amount)} ({formatPct2(seg.share)})
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-center text-[11px] font-medium text-slate-600 print:text-[8px]">
        A+B = {formatPct2(ab)} | C = {formatPct2(c)} (Rule ≥ {formatPct2(threshold)}). D excluded.
      </p>
    </div>
  );
}
